// useNavigationBlocker.js
import { useEffect, useState } from "react";
import { useLocation, matchPath } from "react-router-dom";
import useGeolocationPermission from "./useGeolocationPermission";

/**
 * Handles:
 *  - Browser unload blocking (refresh, tab close)
 *  - Safari 1-second geolocation enable delay
 *  - Ignored routes where blocking is skipped
 *  - Geolocation permission requests
 */
export default function useNavigationBlocker(
  ignoredRoutes = [],
  dynamicIgnoredRoutes = [],
) {
  const location = useLocation();
  const [geolocationEnabled, setGeolocationEnabled] = useState(false);

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Helper: Should we block unload for current path?
  const shouldBlock = () => {
    if (ignoredRoutes.includes(location.pathname)) return false;
    return !dynamicIgnoredRoutes.some((pattern) =>
      matchPath({ path: pattern, end: true }, location.pathname)
    );
  };

  // Apply Safari delay for geolocation enabling
  useEffect(() => {
    if (!shouldBlock()) {
      // No blocking needed → enable geolocation immediately
      setGeolocationEnabled(true);
      return;
    }

    if (!isSafari) {
      // Non-Safari browsers → enable geolocation immediately
      setGeolocationEnabled(true);
    } else {
      // Safari → delay enabling by 1s to avoid dialog freeze
      const timer = setTimeout(() => {
        setGeolocationEnabled(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, isSafari]);

  // Request geolocation permission when allowed
  useGeolocationPermission(() => {}, geolocationEnabled);

  // Handle beforeunload blocking
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!shouldBlock()) return;
      event.preventDefault();
      event.returnValue = ""; // Required for browser prompt
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [location.pathname]);
}
